import { useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, ChevronLeft, ChevronRight, MapPin } from "lucide-react";
import { CALENDAR_HEARINGS, DEMO_TODAY } from "@/data/demo";
import { Card } from "@/components/common";
import { cn } from "@/lib/utils";

const WEEK = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const pad = (n) => String(n).padStart(2, "0");
const iso = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;
const byDate = CALENDAR_HEARINGS.reduce((acc, h) => ({ ...acc, [h.date]: [...(acc[h.date] || []), h] }), {});

const monthCells = (y, m) => {
  const lead = (new Date(y, m, 1).getDay() + 6) % 7;
  const days = new Date(y, m + 1, 0).getDate();
  return [...Array(lead).fill(null), ...Array.from({ length: days }, (_, i) => i + 1)];
};

const DayCell = ({ day, date, selected, onSelect }) => {
  const count = byDate[date]?.length || 0;
  const isToday = date === DEMO_TODAY;
  return (
    <button data-testid={`calendar-day-${date}`} onClick={() => onSelect(date)}
      className={cn("relative flex h-11 sm:h-14 flex-col items-center justify-center rounded-xl text-sm font-semibold transition-colors",
        selected ? "bg-navy text-white" : isToday ? "bg-amber-50 text-gold ring-1 ring-amber-300" : "text-slate-600 hover:bg-slate-100")}>
      {day}
      {count > 0 && (
        <span className="absolute bottom-1.5 flex gap-0.5">
          {Array.from({ length: Math.min(count, 3) }).map((_, i) => <span key={i} className={cn("h-1.5 w-1.5 rounded-full", selected ? "bg-amber-400" : "bg-gold")} />)}
        </span>
      )}
    </button>
  );
};

const DayList = ({ date }) => {
  const items = byDate[date] || [];
  const label = new Date(`${date}T00:00:00`).toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" });
  return (
    <div data-testid="calendar-day-hearings" className="rounded-2xl bg-slate-50 p-4">
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      {items.length === 0 && <p className="mt-3 text-sm text-slate-400">No hearings scheduled.</p>}
      <div className="mt-3 flex flex-col gap-2">
        {items.map((h) => (
          <Link key={h.id} to={`/cases/${h.caseId}`} data-testid={`calendar-hearing-${h.id}`} className="block rounded-xl border border-slate-200 bg-white p-3 transition-colors hover:border-amber-300">
            <div className="text-xs font-bold text-gold">{h.time}</div>
            <div className="mt-0.5 truncate text-sm font-semibold text-navy">{h.title}</div>
            <div className="mt-0.5 flex min-w-0 items-center gap-1 text-xs text-slate-500"><MapPin size={12} className="shrink-0" /><span className="truncate">{h.court}</span></div>
            <div className="mt-1 text-xs text-slate-600">{h.purpose}</div>
          </Link>
        ))}
      </div>
    </div>
  );
};

export const HearingCalendar = () => {
  const [month, setMonth] = useState(() => new Date(`${DEMO_TODAY}T00:00:00`));
  const [selected, setSelected] = useState(DEMO_TODAY);
  const y = month.getFullYear();
  const m = month.getMonth();
  const shift = (d) => setMonth(new Date(y, m + d, 1));
  const monthCount = CALENDAR_HEARINGS.filter((h) => h.date.startsWith(`${y}-${pad(m + 1)}`)).length;

  return (
    <Card data-testid="hearing-calendar" className="mt-6 min-w-0 p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <CalendarDays size={18} className="text-gold" />
          <h2 className="whitespace-nowrap font-heading text-lg font-semibold text-navy">Hearing calendar</h2>
        </div>
        <div className="flex items-center gap-1">
          <button data-testid="calendar-prev-month" onClick={() => shift(-1)} aria-label="Previous month" className="grid h-10 w-10 place-items-center rounded-xl text-slate-500 hover:bg-slate-100"><ChevronLeft size={18} /></button>
          <span data-testid="calendar-month-label" className="w-32 text-center text-sm font-semibold text-navy">{month.toLocaleDateString("en-IN", { month: "long", year: "numeric" })}</span>
          <button data-testid="calendar-next-month" onClick={() => shift(1)} aria-label="Next month" className="grid h-10 w-10 place-items-center rounded-xl text-slate-500 hover:bg-slate-100"><ChevronRight size={18} /></button>
        </div>
      </div>
      <p className="mt-1 text-xs text-slate-500">{monthCount} {monthCount === 1 ? "hearing" : "hearings"} this month</p>
      <div className="mt-4 grid gap-5 lg:grid-cols-[1fr_300px]">
        <div className="grid grid-cols-7 gap-1">
          {WEEK.map((w) => <div key={w} className="pb-1 text-center text-[11px] font-semibold uppercase text-slate-400">{w}</div>)}
          {monthCells(y, m).map((d, i) => d
            ? <DayCell key={i} day={d} date={iso(y, m, d)} selected={selected === iso(y, m, d)} onSelect={setSelected} />
            : <div key={i} />)}
        </div>
        <DayList date={selected} />
      </div>
    </Card>
  );
};
